import type { IContact } from 'src/pages/contacts/contacts.types';

import { toast } from 'sonner';
import { useBoolean } from 'minimal-shared/hooks';
import React, { useRef, useState, useEffect } from 'react';

import { Box, Paper, Avatar, Tooltip, useTheme, Typography, IconButton } from '@mui/material';

import {
  getApiErrorCode,
  CONTACT_PHONE_EXISTS,
  getExistingContactId,
} from 'src/utils/error-helpers';

import { useTranslate } from 'src/locales';
import { truncateText } from 'src/pages/chat/utils/text-formatting';
import { useCustomField } from 'src/pages/setting/hooks/use-custom-field';
import { useCreateContactMutation } from 'src/pages/contacts/slice/contactsApi';
import { ContactAddEditForm } from 'src/pages/contacts/components/contact-add-edit-form';

import { Iconify } from 'src/components/iconify';

import { DTMFButtons } from './call-dtmf';
import { TransferUser } from './transfer-user';
import { useCallContext } from '../hooks/use-call-context';

export function CallPopup() {
  const {
    incomingCall,
    rejectCall,
    pauseCall,
    resumeCall,
    muteCall,
    unmuteCall,
    isMuted,
    isPause,
    transferCall,
    sendDTMF,
    CallPopupMethod,
    callUserDetails,
    callDuration,
    isCallConnected,
  } = useCallContext();

  const { t } = useTranslate('translation');
  const contactMenu = useBoolean();
  const [createContact, { isLoading: isCreating, error: createError }] = useCreateContactMutation();
  // Contact custom fields (incl. required ones) for the "create contact" form; loaded when it opens
  const { _fieldList: contactCustomFields } = useCustomField({
    moduleName: contactMenu.value ? 'contact' : '',
  });
  const [selectedUser, setSelectedUser] = useState<IContact | undefined>(undefined);
  const [dialedNumber, setDialedNumber] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorElTransfer, setAnchorElTransfer] = useState<HTMLElement | null>(null);

  const keys = [
    { main: '1', sub: '' },
    { main: '2', sub: 'ABC' },
    { main: '3', sub: 'DEF' },
    { main: '4', sub: 'GHI' },
    { main: '5', sub: 'JKL' },
    { main: '6', sub: 'MNO' },
    { main: '7', sub: 'PQRS' },
    { main: '8', sub: 'TUV' },
    { main: '9', sub: 'WXYZ' },
    { main: '*', sub: '' },
    { main: '0', sub: '' },
    { main: '#', sub: '' },
  ];

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio('/sounds/keypress.mp3');
    audioRef.current.load();
  }, []);

  const handleDTMF = (key: string) => {
    setDialedNumber((prev) => prev + key);
    sendDTMF(key);

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.error('Audio play error:', err);
      });
    }
  };

  const handleKeypadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleCloseKeypad = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const theme = useTheme();

  const handleTransferMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTransfer(anchorElTransfer ? null : event.currentTarget);
  };

  const handleCloseTransfer = () => {
    setAnchorElTransfer(null);
  };

  const openTransfer = Boolean(anchorElTransfer);

  const handleSubmitData = async (account: Partial<IContact>, assignedPayload?: any) => {
    function convertNullToString(obj: any) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, value === '' ? null : value])
      );
    }
    try {
      // The form passes the (required) assignees separately, as on the Contacts page
      await createContact({ ...convertNullToString(account), ...assignedPayload })
        .unwrap()
        .then((res) => {
          toast.success(t('CONTACT_CREATED_SUCCESSFULLY'));
        })
        .catch((err) => {
          if (getApiErrorCode(err) !== CONTACT_PHONE_EXISTS) {
            toast.error(t('CONTACT_CREATION_FAILED'));
          } else if (getExistingContactId(err)) {
            toast.error(t('CONTACT_PHONE_EXISTS_MESSAGE'));
          } else {
            toast.error(t('CONTACT_PHONE_OWNED_BY_OTHER_AGENT_MESSAGE'));
          }
        });

      contactMenu.onFalse();
    } catch (err) {
      console.error('Contact (onSubmit) Error:', err);
    }
  };

  const onCloseHandler = () => {
    contactMenu.onFalse();
  };

  useEffect(() => {
    if (!incomingCall) {
      setAnchorEl(null);
      setAnchorElTransfer(null);
    }
  }, [incomingCall]);

  useEffect(() => {
    if (contactMenu.value && !callUserDetails && incomingCall?.number) {
      setSelectedUser({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        city: '',
        pincode: '',
        tenantId: '',
        crNumber: '',
        website: '',
        country: '',
        ProfileURL: null,
        createdAt: '',
        updatedAt: '',
        companyId: '',
        assignedId: '',
        assignedType: '',
        nationalId: '',
        labels: [],
        phone: `+${incomingCall.number}`,
      });
    } else if (!contactMenu.value) {
      setSelectedUser(undefined);
    }

    return () => {
      setSelectedUser(undefined);
    };
  }, [contactMenu.value, incomingCall?.number]);

  const displayName =
    callUserDetails?.firstName || callUserDetails?.lastName || callUserDetails?.name
      ? `${callUserDetails?.firstName || ''} ${callUserDetails?.lastName || ''}`.trim() ||
        callUserDetails?.name
      : callUserDetails?.phone || incomingCall?.username || 'Unknown';

  if (!CallPopupMethod.value || !incomingCall) {
    return null;
  }

  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    
    const mStr = String(m).padStart(2, '0');
    const sStr = String(s).padStart(2, '0');
    
    if (h > 0) {
      const hStr = String(h).padStart(2, '0');
      return `${hStr}:${mStr}:${sStr}`;
    }
    return `${mStr}:${sStr}`;
  };

  return (
    <>
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1250, // Higher than app bar (1100) and drawer (1200), but lower than popover/tooltip (1300+)
          width: '450px',
          maxWidth: 'calc(100% - 32px)',
          borderRadius: 2,
          border: (tTheme) => `1px solid ${tTheme.palette.divider}`,
          p: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
          }}
        >
          {/* Caller Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: { xs: '100%', sm: '45%' },
              gap: 2,
            }}
          >
            <Avatar
              alt={callUserDetails?.firstName || callUserDetails?.lastName || callUserDetails?.name}
              src={
                callUserDetails?.ProfileURL || callUserDetails?.firstName || callUserDetails?.name
              }
              sx={{ width: 48, height: 48, ml: 1 }}
            />
            <Box>
              <Tooltip title={displayName}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {truncateText(displayName, 15)}
                </Typography>
              </Tooltip>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                {callUserDetails?.phone || incomingCall?.number || 'N/A'}
              </Typography>

              {/* Connected timer / Connecting status */}
              {isCallConnected ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      animation: 'pulse 1.5s infinite ease-in-out',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.3 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.3 },
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {formatDuration(callDuration)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'medium', mt: 0.5, display: 'block' }}>
                  Connecting...
                </Typography>
              )}
            </Box>
          </Box>

          {/* Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              width: { xs: '100%', sm: '55%' },
            }}
          >
            {!callUserDetails && incomingCall?.number && (
              <Tooltip title={`${t('CREATE')} ${t('CONTACT')}`}>
                <IconButton onClick={contactMenu.onTrue}>
                  <Iconify icon="mdi-user-box" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('KEYPAD')}>
              <IconButton onClick={handleKeypadClick}>
                <Iconify icon="famicons:keypad-sharp" />
              </IconButton>
            </Tooltip>

            {isMuted ? (
              <Tooltip title={t('UNMUTE')}>
                <IconButton onClick={unmuteCall} color="error">
                  <Iconify icon="ep:mute" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('MUTE')}>
                <IconButton onClick={muteCall}>
                  <Iconify icon="solar:microphone-bold" />
                </IconButton>
              </Tooltip>
            )}

            {isPause ? (
              <Tooltip title={t('RESUME')}>
                <IconButton onClick={resumeCall} color="warning">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path fill="currentColor" d="M6 18V6h2v12zm4 0l10-6l-10-6z" />
                  </svg>
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('HOLD')}>
                <IconButton onClick={pauseCall}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path fill="currentColor" d="M14 19V5h4v14zm-8 0V5h4v14z" />
                  </svg>
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t('TRANSFER')}>
              <IconButton onClick={handleTransferMenu}>
                <Iconify icon="mingcute-transfer-line" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('END_CALL')}>
              <IconButton
                onClick={rejectCall}
                color="error"
                sx={{
                  bgcolor: 'error.lighter',
                  '&:hover': {
                    bgcolor: 'error.light',
                  },
                }}
              >
                <Iconify icon="solar:end-call-bold" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Keypad Popover */}
      <DTMFButtons
        open={open && CallPopupMethod.value && Boolean(incomingCall)}
        anchorEl={anchorEl}
        handleClose={handleCloseKeypad}
        keys={keys}
        handleDTMF={handleDTMF}
        theme={theme}
      />
      <TransferUser
        open={openTransfer && CallPopupMethod.value && Boolean(incomingCall)}
        anchorEl={anchorElTransfer}
        handleClose={handleCloseTransfer}
        onTransfer={transferCall}
      />

      {contactMenu.value && (
        <ContactAddEditForm
          open={contactMenu.value}
          onClose={onCloseHandler}
          handleSubmitData={handleSubmitData}
          selectedUser={selectedUser}
          customFields={contactCustomFields}
          isCallPopup
        />
      )}
    </>
  );
}
